import {
  Network,
  Server,
  Lock,
  Shield,
  Database,
  GitBranch,
  Terminal,
  Layers,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Info,
  Globe,
  Key,
  Users,
  FolderTree,
  FileText,
  Bot,
  Settings,
  LayoutGrid,
  Zap,
  MessageSquare,
} from 'lucide-react'

export function RoutingSection() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text mb-2 flex items-center gap-3">
          <Network className="w-7 h-7 text-market" />
          Routing Architecture
        </h1>
        <p className="text-text-secondary">
          Comprehensive guide to iSpot's full-stack routing system, covering backend API routes, frontend navigation,
          middleware chains, and security architecture.
        </p>
      </div>

      {/* Quick Stats Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { value: '150+', label: 'API Endpoints', icon: Server, color: 'market' },
          { value: '19', label: 'Route Files', icon: FileText, color: 'business' },
          { value: '13', label: 'Frontend Views', icon: Globe, color: 'solution' },
          { value: '4', label: 'Auth Layers', icon: Shield, color: 'market' },
        ].map((stat, idx) => {
          const Icon = stat.icon
          return (
            <div key={idx} className="bg-white rounded-xl p-4 border border-warm-200">
              <div className={`flex items-center justify-between mb-2`}>
                <Icon className={`w-5 h-5 text-${stat.color}`} />
                <div className="text-2xl font-bold text-text">{stat.value}</div>
              </div>
              <div className="text-xs text-text-secondary uppercase tracking-wide">{stat.label}</div>
            </div>
          )
        })}
      </div>

      {/* Architecture Overview */}
      <div className="bg-gradient-to-br from-market/5 to-business/5 rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-market" />
          Multi-Tenant Architecture
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-market/10 flex items-center justify-center flex-shrink-0">
              <span className="text-market font-bold">1</span>
            </div>
            <div>
              <div className="font-medium text-text">Platform Layer</div>
              <div className="text-sm text-text-secondary">Global resources, super admin, health checks</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-business/10 flex items-center justify-center flex-shrink-0">
              <span className="text-business font-bold">2</span>
            </div>
            <div>
              <div className="font-medium text-text">Account Layer</div>
              <div className="text-sm text-text-secondary">Tenant isolation, role-based access (owner, admin, member)</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-solution/10 flex items-center justify-center flex-shrink-0">
              <span className="text-solution font-bold">3</span>
            </div>
            <div>
              <div className="font-medium text-text">Project Layer</div>
              <div className="text-sm text-text-secondary">Project-scoped resources, granular RBAC, named graph isolation</div>
            </div>
          </div>
        </div>
      </div>

      {/* Backend API Routes */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-text flex items-center gap-2">
          <Server className="w-6 h-6 text-business" />
          Backend API Routes
        </h2>

        {/* Route Groups */}
        <div className="grid gap-4">
          {/* Authentication */}
          <RouteCard
            title="Authentication"
            path="/api/auth"
            icon={Lock}
            color="market"
            description="User registration, login, session management"
            endpoints={[
              { method: 'POST', path: '/register', auth: false, desc: 'Create user + account' },
              { method: 'POST', path: '/login', auth: false, desc: 'Sign in' },
              { method: 'POST', path: '/logout', auth: true, desc: 'Sign out' },
              { method: 'GET', path: '/me', auth: true, desc: 'Current user + accounts' },
              { method: 'GET', path: '/users', auth: 'admin', desc: 'List all users (admin only)' },
            ]}
            security={[
              'BCrypt password hashing (cost: 12)',
              'HTTP-only session cookies',
              'Bearer token support for cross-origin',
              'CSRF protection via SameSite cookies',
            ]}
          />

          {/* Accounts */}
          <RouteCard
            title="Account Management"
            path="/api/accounts"
            icon={Users}
            color="business"
            description="Multi-tenant account operations and member management"
            endpoints={[
              { method: 'GET', path: '/', auth: true, desc: 'List user\'s accounts' },
              { method: 'POST', path: '/', auth: true, desc: 'Create new account' },
              { method: 'GET', path: '/:accountId', auth: true, desc: 'Get account details' },
              { method: 'PATCH', path: '/:accountId', auth: 'admin', desc: 'Update account' },
              { method: 'GET', path: '/:accountId/members', auth: true, desc: 'List members' },
              { method: 'POST', path: '/:accountId/members', auth: 'admin', desc: 'Invite member' },
            ]}
            roles={[
              { name: 'Owner', desc: 'Full control, cannot be removed' },
              { name: 'Admin', desc: 'Manage members, update settings' },
              { name: 'Member', desc: 'View access only' },
            ]}
          />

          {/* Projects */}
          <RouteCard
            title="Project Management"
            path="/api/accounts/:accountId/projects"
            icon={FolderTree}
            color="solution"
            description="Project-scoped resources with granular RBAC"
            endpoints={[
              { method: 'GET', path: '/', auth: true, desc: 'List account projects' },
              { method: 'POST', path: '/', auth: true, desc: 'Create project (becomes admin)' },
              { method: 'GET', path: '/:projectId', auth: 'member', desc: 'Get project details' },
              { method: 'PATCH', path: '/:projectId', auth: 'project:manage', desc: 'Update project' },
              { method: 'DELETE', path: '/:projectId', auth: 'project:manage', desc: 'Delete project + graph' },
            ]}
            roles={[
              { name: 'Admin', desc: 'All permissions' },
              { name: 'Product Manager', desc: 'Ontology read/write, reports' },
              { name: 'Sales', desc: 'Read-only + reports' },
              { name: 'Viewer', desc: 'Read-only access' },
            ]}
          />

          {/* SPARQL */}
          <RouteCard
            title="SPARQL Query Engine"
            path="/api/accounts/:accountId/projects/:projectId/sparql"
            icon={Database}
            color="market"
            description="Execute semantic queries with domain-level permissions"
            endpoints={[
              { method: 'POST', path: '/query', auth: 'ontology:read:*', desc: 'SELECT/ASK/CONSTRUCT queries' },
              { method: 'POST', path: '/update', auth: 'ontology:write:*', desc: 'INSERT/DELETE operations' },
            ]}
            security={[
              'Domain-level permission checks (market, business, solution)',
              'Named graph isolation per project',
              'URI extraction and permission validation',
              'Query type detection (SELECT/ASK/CONSTRUCT/INSERT/DELETE)',
            ]}
          />

          {/* Sources/Documents */}
          <RouteCard
            title="Document Analysis"
            path="/api/accounts/:accountId/projects/:projectId/sources"
            icon={FileText}
            color="business"
            description="Document upload, AI analysis, facet extraction"
            endpoints={[
              { method: 'GET', path: '/', auth: true, desc: 'List sources' },
              { method: 'POST', path: '/', auth: true, desc: 'Create source + analyze' },
              { method: 'GET', path: '/:sourceId', auth: true, desc: 'Get source details' },
              { method: 'POST', path: '/:sourceId/analyze', auth: true, desc: 'Re-analyze document' },
              { method: 'GET', path: '/:sourceId/facets', auth: true, desc: 'Get extracted facets' },
              { method: 'POST', path: '/facets/:facetId/accept', auth: true, desc: 'Create entity from facet' },
            ]}
            features={[
              'Multi-pass AI analysis (entities → facets → visual)',
              'Facet type filtering (52 types across 10 categories)',
              'Review board UX for entity creation',
              'Bidirectional entity-facet linking',
            ]}
          />

          {/* AI Workflows */}
          <RouteCard
            title="AI Workflows & Prompts"
            path="/api/workflows"
            icon={Bot}
            color="solution"
            description="AI workflow orchestration and prompt management"
            endpoints={[
              { method: 'GET', path: '/workflows', auth: true, desc: 'List workflows' },
              { method: 'GET', path: '/workflows/:workflowId/full', auth: true, desc: 'Get workflow with steps' },
              { method: 'GET', path: '/prompts', auth: true, desc: 'List prompts' },
              { method: 'GET', path: '/prompts/:promptKey', auth: true, desc: 'Get prompt by key' },
              { method: 'GET', path: '/agents', auth: true, desc: 'List agents' },
              { method: 'POST', path: '/agents/:agentId/trigger', auth: true, desc: 'Trigger agent manually' },
            ]}
            features={[
              '73 sophisticated prompts (48 for Discovery Coach)',
              '4 workflows (Discovery, AI Coach, Document Analysis, Discovery Coach)',
              'Account-level prompt customization',
              'Workflow event → agent triggers',
            ]}
          />

          {/* CRM */}
          <RouteCard
            title="CRM (Customers & Persons)"
            path="/api/accounts/:accountId/projects/:projectId/customers"
            icon={Users}
            color="market"
            description="Customer relationship management"
            endpoints={[
              { method: 'GET', path: '/customers', auth: true, desc: 'List customers' },
              { method: 'POST', path: '/customers', auth: true, desc: 'Create customer' },
              { method: 'GET', path: '/persons', auth: true, desc: 'List persons/contacts' },
              { method: 'POST', path: '/persons', auth: true, desc: 'Create contact' },
            ]}
          />

          {/* Discovery */}
          <RouteCard
            title="Discovery Workflow"
            path="/api/accounts/:accountId/projects/:projectId/discovery"
            icon={Zap}
            color="business"
            description="Guided discovery process endpoints"
            endpoints={[
              { method: 'GET', path: '/phases', auth: true, desc: 'List discovery phases' },
              { method: 'GET', path: '/phases/:phaseId', auth: true, desc: 'Get phase details' },
              { method: 'POST', path: '/ai-assist', auth: true, desc: 'AI coaching for entity creation' },
            ]}
          />
        </div>
      </div>

      {/* Middleware Chain */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-text flex items-center gap-2">
          <GitBranch className="w-6 h-6 text-market" />
          Middleware Architecture
        </h2>

        <div className="bg-white rounded-xl p-6 border border-warm-200">
          <div className="space-y-4">
            <MiddlewareStep
              number={1}
              title="Global Middleware"
              color="market"
              items={[
                { icon: Terminal, text: 'Request logging', desc: 'Hono logger middleware' },
                { icon: Globe, text: 'CORS handling', desc: 'Origin: http://localhost:5174 (dev)' },
              ]}
            />
            <ArrowRight className="w-5 h-5 text-text-secondary mx-auto" />
            <MiddlewareStep
              number={2}
              title="Authentication Layer"
              color="business"
              items={[
                { icon: Lock, text: 'requireAuth', desc: 'Validates session cookie or bearer token' },
                { icon: Key, text: 'User injection', desc: 'Adds c.get(\'user\') to context' },
              ]}
            />
            <ArrowRight className="w-5 h-5 text-text-secondary mx-auto" />
            <MiddlewareStep
              number={3}
              title="Account Layer"
              color="solution"
              items={[
                { icon: Users, text: 'requireAccount', desc: 'Verifies account membership' },
                { icon: Shield, text: 'Role check', desc: 'Adds c.get(\'accountRole\') to context' },
              ]}
            />
            <ArrowRight className="w-5 h-5 text-text-secondary mx-auto" />
            <MiddlewareStep
              number={4}
              title="Project Layer"
              color="market"
              items={[
                { icon: FolderTree, text: 'requireProject', desc: 'Verifies project membership' },
                { icon: CheckCircle, text: 'Permission check', desc: 'Domain-level RBAC validation' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-text flex items-center gap-2">
          <Shield className="w-6 h-6 text-business" />
          Security Architecture
        </h2>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Authentication */}
          <SecurityCard
            title="Authentication"
            icon={Lock}
            color="market"
            items={[
              { status: 'good', text: 'BCrypt password hashing (cost: 12)' },
              { status: 'good', text: 'HTTP-only session cookies' },
              { status: 'good', text: 'SameSite=Lax CSRF protection' },
              { status: 'good', text: 'Bearer token support for API clients' },
              { status: 'warning', text: 'Hardcoded admin secret (ispot-admin-2024)' },
            ]}
          />

          {/* Authorization */}
          <SecurityCard
            title="Authorization"
            icon={Shield}
            color="business"
            items={[
              { status: 'good', text: '4-layer permission model' },
              { status: 'good', text: 'Domain-level SPARQL access control' },
              { status: 'good', text: 'Role-based access (owner/admin/member)' },
              { status: 'good', text: 'Project-level granular RBAC' },
              { status: 'info', text: 'Permissions checked on every request' },
            ]}
          />

          {/* Tenant Isolation */}
          <SecurityCard
            title="Tenant Isolation"
            icon={Database}
            color="solution"
            items={[
              { status: 'good', text: 'Database: account_id + project_id columns' },
              { status: 'good', text: 'Graph: Named graphs per project' },
              { status: 'good', text: 'Middleware: Account membership verification' },
              { status: 'good', text: 'Frontend: Project-scoped localStorage' },
              { status: 'info', text: 'Zero cross-tenant data leakage' },
            ]}
          />

          {/* Input Validation */}
          <SecurityCard
            title="Input Validation"
            icon={CheckCircle}
            color="market"
            items={[
              { status: 'good', text: 'Zod schema validation on all inputs' },
              { status: 'good', text: 'SQL injection prevented (Drizzle ORM)' },
              { status: 'good', text: 'SPARQL injection via parameterization' },
              { status: 'warning', text: 'No rate limiting configured' },
              { status: 'warning', text: 'Missing security headers (CSP, HSTS)' },
            ]}
          />
        </div>
      </div>

      {/* Frontend Routes */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-text flex items-center gap-2">
          <Globe className="w-6 h-6 text-solution" />
          Frontend Routes
        </h2>

        <div className="bg-white rounded-xl p-6 border border-warm-200">
          <div className="space-y-3">
            {[
              { path: '/', name: 'Dashboard', auth: true, desc: 'Account & project overview' },
              { path: '/ontology', name: 'Ontology Browser', auth: true, desc: 'Navigate knowledge graph' },
              { path: '/graph', name: 'Graph View', auth: true, desc: 'Visual graph representation' },
              { path: '/tree', name: 'Tree View', auth: true, desc: 'Hierarchical ontology view' },
              { path: '/discovery', name: 'Discovery Workflow', auth: true, desc: '5-phase guided discovery' },
              { path: '/discovery-coach', name: 'Discovery Coach', auth: true, desc: '12-step AI-guided process' },
              { path: '/sources', name: 'Document Analysis', auth: true, desc: 'Upload & analyze documents' },
              { path: '/sources/:id', name: 'Source Detail', auth: true, desc: 'Facet review board' },
              { path: '/customers', name: 'CRM', auth: true, desc: 'Customers & contacts' },
              { path: '/canvas', name: 'Canvas Views', auth: true, desc: 'Business model canvas, VPC' },
              { path: '/settings', name: 'Settings', auth: true, desc: 'Project configuration' },
              { path: '/help', name: 'Help & Docs', auth: true, desc: 'Documentation hub' },
              { path: '/login', name: 'Login', auth: false, desc: 'Authentication page' },
            ].map((route, idx) => (
              <div key={idx} className="flex items-center gap-3 py-2 border-b border-warm-100 last:border-0">
                <code className="text-sm bg-warm-100 px-2 py-1 rounded font-mono text-market flex-shrink-0">
                  {route.path}
                </code>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text">{route.name}</div>
                  <div className="text-xs text-text-secondary truncate">{route.desc}</div>
                </div>
                {route.auth && (
                  <Lock className="w-4 h-4 text-business flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-gradient-to-br from-market/5 to-solution/5 rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-market" />
          Routing Best Practices
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-market flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">Hierarchical structure:</span> Platform → Account → Project → Resource
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-market flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">RESTful design:</span> Predictable resource-based URLs
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-market flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">Middleware composition:</span> Layered security with context injection
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-business flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">Tenant isolation:</span> Zero cross-account data leakage
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-business flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">Type safety:</span> Zod validation on all inputs
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-business flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <span className="font-medium text-text">Consistent patterns:</span> Unified response formats
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Reference */}
      <div className="bg-white rounded-xl p-6 border border-warm-200">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-solution" />
          Technical Reference
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">Full documentation:</span>
            <code className="bg-warm-100 px-2 py-1 rounded text-market font-mono text-xs">
              /ontology-browser/docs/ROUTING-ARCHITECTURE-REVIEW.md
            </code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">Backend routes:</span>
            <code className="bg-warm-100 px-2 py-1 rounded text-market font-mono text-xs">
              /ontology-browser/api/src/routes/
            </code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">API entry point:</span>
            <code className="bg-warm-100 px-2 py-1 rounded text-market font-mono text-xs">
              /ontology-browser/api/src/index.ts
            </code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-secondary">Frontend routing:</span>
            <code className="bg-warm-100 px-2 py-1 rounded text-market font-mono text-xs">
              /ontology-browser/src/App.tsx
            </code>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper Components

function RouteCard({
  title,
  path,
  icon: Icon,
  color,
  description,
  endpoints,
  security,
  roles,
  features,
}: {
  title: string
  path: string
  icon: any
  color: string
  description: string
  endpoints: Array<{ method: string; path: string; auth: boolean | string; desc: string }>
  security?: string[]
  roles?: Array<{ name: string; desc: string }>
  features?: string[]
}) {
  return (
    <div className="bg-white rounded-xl p-5 border border-warm-200 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text mb-1">{title}</h3>
          <code className="text-xs bg-warm-100 px-2 py-1 rounded font-mono text-market block mb-2 truncate">
            {path}
          </code>
          <p className="text-sm text-text-secondary">{description}</p>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-2 mb-4">
        {endpoints.map((endpoint, idx) => (
          <div key={idx} className="flex items-start gap-2 text-xs">
            <span
              className={`px-2 py-0.5 rounded font-mono flex-shrink-0 ${
                endpoint.method === 'GET'
                  ? 'bg-market/10 text-market'
                  : endpoint.method === 'POST'
                  ? 'bg-business/10 text-business'
                  : endpoint.method === 'PATCH'
                  ? 'bg-solution/10 text-solution'
                  : 'bg-red-100 text-red-600'
              }`}
            >
              {endpoint.method}
            </span>
            <code className="font-mono text-text-secondary flex-shrink-0">{endpoint.path}</code>
            <span className="text-text-secondary flex-1 min-w-0 truncate">{endpoint.desc}</span>
            {endpoint.auth && (
              <Lock
                className="w-3 h-3 text-business flex-shrink-0"
              />
            )}
          </div>
        ))}
      </div>

      {/* Security Features */}
      {security && (
        <div className="border-t border-warm-100 pt-3">
          <div className="text-xs font-medium text-text mb-2 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Security
          </div>
          <ul className="space-y-1">
            {security.map((item, idx) => (
              <li key={idx} className="text-xs text-text-secondary flex items-start gap-1.5">
                <CheckCircle className="w-3 h-3 text-market flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Roles */}
      {roles && (
        <div className="border-t border-warm-100 pt-3">
          <div className="text-xs font-medium text-text mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" />
            Roles
          </div>
          <div className="space-y-1">
            {roles.map((role, idx) => (
              <div key={idx} className="text-xs">
                <span className="font-medium text-text">{role.name}:</span>{' '}
                <span className="text-text-secondary">{role.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      {features && (
        <div className="border-t border-warm-100 pt-3">
          <div className="text-xs font-medium text-text mb-2 flex items-center gap-1">
            <Zap className="w-3 h-3" />
            Features
          </div>
          <ul className="space-y-1">
            {features.map((item, idx) => (
              <li key={idx} className="text-xs text-text-secondary flex items-start gap-1.5">
                <CheckCircle className="w-3 h-3 text-solution flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function MiddlewareStep({
  number,
  title,
  color,
  items,
}: {
  number: number
  title: string
  color: string
  items: Array<{ icon: any; text: string; desc: string }>
}) {
  return (
    <div className="flex items-start gap-4">
      <div className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center flex-shrink-0`}>
        <span className={`text-${color} font-bold`}>{number}</span>
      </div>
      <div className="flex-1">
        <div className="font-medium text-text mb-2">{title}</div>
        <div className="space-y-2">
          {items.map((item, idx) => {
            const Icon = item.icon
            return (
              <div key={idx} className="flex items-start gap-2">
                <Icon className="w-4 h-4 text-text-secondary flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-text">{item.text}</span>
                  <span className="text-text-secondary"> - {item.desc}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function SecurityCard({
  title,
  icon: Icon,
  color,
  items,
}: {
  title: string
  icon: any
  color: string
  items: Array<{ status: 'good' | 'warning' | 'info'; text: string }>
}) {
  return (
    <div className="bg-white rounded-xl p-5 border border-warm-200">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}`} />
        </div>
        <h4 className="font-semibold text-text">{title}</h4>
      </div>
      <ul className="space-y-2">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            {item.status === 'good' && <CheckCircle className="w-4 h-4 text-market flex-shrink-0 mt-0.5" />}
            {item.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />}
            {item.status === 'info' && <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
            <span className="text-text-secondary">{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
